import { logMessage } from '../utils/helpers';

// Define the type for headers (if applicable).
interface Headers {
    [key: string]: string;
}

// Define the shape of the parameters for the request
interface RequestParams {
    [key: string]: string;
}

// Outcome of a request, with enough detail to tell a definitive answer apart
// from a transient one. `status` is 0 when no response was received at all
// (network error or abort), in which case `aborted` says which.
export interface RequestMeta {
    status: number;
    ok: boolean;
    aborted: boolean;
    body: any;
    error?: string;
}

export class InHouseRequester {
    private _headers: Headers | undefined;
    private logout_function: () => void;

    constructor(headers: Headers | undefined, logout_function: () => void) {
        if (headers) {
            this._headers = headers;
        }
        this.logout_function = logout_function;
    }

    // The smallest header set an authenticated JSON POST needs. Both of these are
    // CORS-safelisted-by-configuration on every vortex endpoint, so a call using
    // only these cannot be blocked by a narrow preflight. Pass to sendRequest as
    // `headersOverride` for endpoints that reject our custom headers, rather than
    // leaking the bearer out of this class.
    minimalJsonHeaders(): Headers {
        const auth = this._headers && this._headers['Authorization'];
        return auth
            ? { 'Authorization': auth, 'Content-Type': 'application/json' }
            : { 'Content-Type': 'application/json' };
    }

    // Define the type for the response of the sendRequest method
    // `headersOverride` REPLACES the instance headers for this one call. Needed
    // because not every endpoint allows the same headers: `data/session_history`
    // permits only Content-Type and Authorization, so sending our usual
    // X-Application-Id / X-Stage fails its CORS preflight and the request never
    // leaves the browser. See the call site in udf-compatible-datafeed-base.
    async sendRequest(url: string, urlPath: string, body?: any, params: RequestParams = {}, method: "GET" | "POST" | "PUT" | "DELETE" = "GET", headersOverride?: Headers): Promise<any> {
        const meta = await this.sendRequestWithMeta(url, urlPath, body, params, method, headersOverride);
        return meta.body;
    }

    // Same request, but the caller also gets the HTTP status and whether the
    // failure was a transport error rather than a response.
    //
    // `sendRequest` deliberately collapses everything to a body (or null), which
    // is right for callers that only need data. It is WRONG for any caller that
    // must tell "the server answered, and the answer is no" apart from "the
    // server did not answer": a 429 and a 5xx both return a JSON body with the
    // field missing, indistinguishable from a legitimately empty result. See
    // _fetchSessionHistory, where that distinction decides whether a transient
    // blip gets cached as a permanent answer.
    //
    // `signal` lets a caller bound the REQUEST's lifetime. Without one, a hung
    // socket never settles, so any in-flight bookkeeping the caller keeps leaks
    // for the life of the page.
    async sendRequestWithMeta(url: string, urlPath: string, body?: any, params: RequestParams = {}, method: "GET" | "POST" | "PUT" | "DELETE" = "GET", headersOverride?: Headers, signal?: AbortSignal): Promise<RequestMeta> {
        logMessage('New request: ' + urlPath);

        const options: RequestInit = { method }; // Start with the HTTP method (default to GET)

        const effectiveHeaders = headersOverride ?? this._headers;
        if (effectiveHeaders) {
            options.headers = effectiveHeaders;
        }

        if (body && method !== "GET") {
            options.body = JSON.stringify(body);
            // A JSON body without this header is not parsed by the API's body
            // parser: every field arrives empty and the endpoint answers as
            // though nothing was sent — a 200 with an empty result, not an
            // error. Copy rather than mutate: `this._headers` is shared by
            // every request and the GETs must not start carrying a body
            // content type.
            options.headers = { ...(effectiveHeaders as any), "Content-Type": "application/json" };
        }

        if (signal) {
            options.signal = signal;
        }

        const fullUrl = new URL(`${url}${urlPath}`);
        if (params && Object.keys(params).length > 0) {
            fullUrl.search = new URLSearchParams(params as Record<string, string>).toString();
        }

        // fullUrl.search = new URLSearchParams(params).toString(); // Attach query parameters

        return fetch(fullUrl.toString(), options)
            .then((response) => {
                if (response.status === 401) {
                    this.logout_function();
                    // Unchanged behaviour: 401 logs out and yields no body.
                    return { status: 401, ok: false, aborted: false, body: null } as RequestMeta;
                }
                return response.text().then((responseText) => {
                    let parsed: any = null;
                    try {
                        parsed = JSON.parse(responseText);
                    } catch (error) {
                        console.error("Failed to parse response:", error);
                    }
                    return { status: response.status, ok: response.ok, aborted: false, body: parsed } as RequestMeta;
                });
            })
            .catch((error) => {
                console.error("Request failed:", error);
                return {
                    status: 0,
                    ok: false,
                    aborted: !!(error && error.name === 'AbortError'),
                    body: null,
                    error: (error && error.message) || String(error),
                } as RequestMeta;
            });
    }

    async getQuotes(url: string, queryString: string): Promise<any> {
        const requestOptions: RequestInit = {
            method: 'GET',
            headers: this._headers,
        };

        return fetch(`${url}analytics/quote?${queryString}`, requestOptions)
            .then((response) => response.text())
            .then((responseText) => {
                try {
                    return JSON.parse(responseText);
                } catch (error) {
                    console.error("Failed to parse quotes response:", error);
                    return null;
                }
            })
            .catch((error) => {
                console.error("Failed to fetch quotes:", error);
                return null;
            });
    }
}
